import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Factory,
  Users,
  Crown,
  Ruler,
  Car,
  Search,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import companyParameterService from '../../services/companyParameterService';
import { confirmDelete } from '../../utils/confirmDelete';
import { useData } from '../../context/DataContext';

type ParamItem = {
  id: string;
  name: string;
  usage: number;
  status: string;
  description?: string;
  range?: string;
  province?: string;
};

type ParameterMap = {
  industries: ParamItem[];
  ownership: ParamItem[];
  sizes: ParamItem[];
  vehicles: ParamItem[];
  countries: ParamItem[];
  provinces: ParamItem[];
  locations: ParamItem[];
};

type Config = {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  fields: string[];
  columns: string[];
};

interface CompanyParameterManagementProps {
  companies?: any[];
  jobs?: any[];
  applications?: any[];
}

export function CompanyParameterManagement({
  companies = [],
  jobs = [],
  applications = []
}: CompanyParameterManagementProps) {
  const { refreshAdminIndustries, refreshAdminCities, refreshAdminCountries, refreshAdminProvinces } = useData();
  const [activeTab, setActiveTab] = useState('industries');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const remoteTabs = ['industries', 'ownership', 'countries', 'provinces', 'locations', 'sizes', 'vehicles'] as const;
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Function to calculate real usage statistics
  const calculateUsageStats = () => {
    const safeCompanies = Array.isArray(companies) ? companies : [];
    const safeJobs = Array.isArray(jobs) ? jobs : [];

    // Helper to normalize strings
    const normalize = (str: any) => String(str || '').toLowerCase().trim();

    // 1. Build company lookup (ID -> Industry) to help resolve job industries
    const companyIndustryMap: Record<string, string> = {};
    safeCompanies.forEach(c => {
      const id = String((c as any)._id || c.id || '');
      const industry = (c as any).industry || (c as any).category || '';
      if (id && industry) companyIndustryMap[id] = industry;
    });

    // 2. Calculate industry usage based on JOBS (primary) + Companies (secondary or separate?)
    // The user requested "proper usage counts", likely job counts for industries.
    const industryUsage: { [key: string]: number } = {};

    // Count jobs per industry
    safeJobs.forEach(job => {
      let industry = job.category; // active job category
      if (!industry) {
        // Fallback to company industry if job has no category
        const compId = String((job as any).company_id || (job as any).companyId || job.company?._id || job.company?.id || '');
        industry = companyIndustryMap[compId];
      }

      if (industry) {
        industryUsage[industry] = (industryUsage[industry] || 0) + 1;
      }
    });

    // Also count industry usage by companies (if a company exists but has no jobs, it should still count? 
    // Usually "usage" in parameters implies used by X entities. Ideally we show both or max. 
    // Let's stick to Job count as it's more dynamic for "Categories". 
    // BUT we should also ensure we don't lose counts if we only have companies.
    // Let's ADD company counts? No, that confuses. Let's stick to Job Counts as per "Category Management" context.
    // However, if the user explicitly mentioned "industry names on company parameter section", they might expect company counts.
    // Let's check the current implementation: it was counting companies.
    // I will switch it to count JOBS because that matches "Category Management" better.
    // If the count is 0, we can check companies. 

    // Actually, let's count both for robust "usage". 
    // If I have 5 jobs in IT, count is 5.
    // If I have 1 company in IT with 0 jobs, count is likely 1 (company usage).
    // Let's use Job Counts primarily.


    // Calculate company size usage
    const sizeUsage: { [key: string]: number } = {};
    safeCompanies.forEach(company => {
      const size = company?.size || company?.company_size || company?.totalEmployees || '';
      if (size) {
        // Normalize size ranges
        let normalizedSize = '';
        if (size.includes('1-10') || size === '1-10' || size === 'Small') normalizedSize = '1-10';
        else if (size.includes('11-50') || size === '11-50') normalizedSize = '11-50';
        else if (size.includes('51-200') || size === '51-200' || size === 'Medium') normalizedSize = '51-200';
        else if (size.includes('201-500') || size === '201-500') normalizedSize = '201-500';
        else if (size.includes('501-1000') || size === '501-1000') normalizedSize = '501-1000';
        else if (size.includes('1000+') || size === '1000+' || size === 'Large') normalizedSize = '1000+';

        if (normalizedSize) {
          sizeUsage[normalizedSize] = (sizeUsage[normalizedSize] || 0) + 1;
        }
      }
    });

    // Calculate ownership/company type usage
    const ownershipUsage: { [key: string]: number } = {};
    safeCompanies.forEach(company => {
      const type = company?.companyType || company?.company_type || company?.ownership || '';
      if (type) {
        ownershipUsage[type] = (ownershipUsage[type] || 0) + 1;
      }
    });

    // Calculate location usage based on companies and jobs
    const locationUsage: { [key: string]: number } = {};
    const countryUsage: { [key: string]: number } = {};
    const provinceUsage: { [key: string]: number } = {};

    safeCompanies.forEach(company => {
      const rawCity = String(company?.city || '').trim();
      const rawLocation = String(company?.location || '').trim();
      const rawCountry = String(company?.country || '').trim();
      const rawProvince = String(company?.province || '').trim();
      const city = rawCity || (rawLocation.includes(',') ? rawLocation.split(',')[0].split(' - ').pop()?.trim() : '');
      const country = rawCountry || (rawLocation.includes(',') ? (rawLocation.split(',').slice(-1)[0] || '').trim() : '');
      const province = rawProvince;
      if (city) {
        locationUsage[city] = (locationUsage[city] || 0) + 1;
      }
      if (country) {
        countryUsage[country] = (countryUsage[country] || 0) + 1;
      }
      if (province) {
        provinceUsage[province] = (provinceUsage[province] || 0) + 1;
      }
    });
    safeJobs.forEach(job => {
      const loc = String(job?.location || '').trim();
      let city = '';
      let country = '';
      if (loc.includes(' - ')) {
        const second = loc.split(' - ')[1] || '';
        city = (second.split(',')[0] || '').trim();
        country = (second.split(',').slice(-1)[0] || '').trim();
      } else if (loc.includes(',')) {
        city = (loc.split(',')[0] || '').trim();
        country = (loc.split(',').slice(-1)[0] || '').trim();
      } else {
        city = loc;
      }
      if (city) {
        locationUsage[city] = (locationUsage[city] || 0) + 1;
      }
      if (country) {
        countryUsage[country] = (countryUsage[country] || 0) + 1;
      }
    });

    // Calculate vehicle benefits usage from jobs
    const vehicleUsage: { [key: string]: number } = {};
    safeJobs.forEach(job => {
      const licenseRequired = typeof job?.license_required === 'boolean'
        ? job.license_required
        : !!job?.licenseRequired;
      const vehicleTypeRaw = job?.vehicle_type || job?.vehicleType || '';
      const vehicleType = normalize(vehicleTypeRaw);

      if (licenseRequired) {
        if (vehicleType) {
          vehicleUsage[vehicleType] = (vehicleUsage[vehicleType] || 0) + 1;
        }
      } else if (licenseRequired === false) {
        vehicleUsage[normalize('No Vehicle')] = (vehicleUsage[normalize('No Vehicle')] || 0) + 1;
      }

      // Optional: benefits-based counting if present on job
      const benefits = Array.isArray(job?.benefits) ? job.benefits : [];
      benefits.forEach((b: any) => {
        const bn = normalize(b);
        if (!bn) return;
        const map: Record<string, string> = {
          'transport allowance': normalize('Transport Allowance'),
          'fuel allowance': normalize('Fuel Allowance'),
          'public transport': normalize('Public Transport'),
          'company vehicle': normalize('Company Vehicle'),
        };
        const key = map[bn] || bn;
        vehicleUsage[key] = (vehicleUsage[key] || 0) + 1;
      });
    });

    return { industryUsage, sizeUsage, ownershipUsage, locationUsage, countryUsage, provinceUsage, vehicleUsage };
  };

  const { industryUsage, sizeUsage, ownershipUsage, locationUsage, countryUsage, provinceUsage, vehicleUsage } = calculateUsageStats();

  // Real data for different company parameters (with calculated usage)
  const [parameters, setParameters] = useState<ParameterMap>({
    industries: [
      { id: '1', name: 'Information Technology', description: 'Software, hardware, and IT services', usage: industryUsage['Information Technology'] || industryUsage['Technology'] || 0, status: 'active' },
      { id: '2', name: 'Banking & Finance', description: 'Banks, financial services, insurance', usage: industryUsage['Banking & Finance'] || industryUsage['Finance'] || 0, status: 'active' },
      { id: '3', name: 'Healthcare & Medical', description: 'Hospitals, clinics, medical services', usage: industryUsage['Healthcare & Medical'] || industryUsage['Healthcare'] || 0, status: 'active' },
      { id: '4', name: 'Education & Training', description: 'Schools, colleges, training institutes', usage: industryUsage['Education & Training'] || industryUsage['Education'] || 0, status: 'active' },
      { id: '5', name: 'Manufacturing', description: 'Production, assembly, industrial', usage: industryUsage['Manufacturing'] || 0, status: 'active' },
      { id: '6', name: 'Retail & E-commerce', description: 'Retail stores, online commerce', usage: industryUsage['Retail & E-commerce'] || industryUsage['Retail'] || 0, status: 'active' },
      { id: '7', name: 'Construction & Real Estate', description: 'Building, property development', usage: industryUsage['Construction & Real Estate'] || industryUsage['Construction'] || 0, status: 'active' },
      { id: '8', name: 'Tourism & Hospitality', description: 'Hotels, restaurants, travel', usage: industryUsage['Tourism & Hospitality'] || industryUsage['Hospitality'] || 0, status: 'active' }
    ],
    countries: [
      { id: 'np', name: 'Nepal', description: 'Country', usage: countryUsage['Nepal'] || 0, status: 'active' }
    ],
    provinces: [
      { id: 'bagmati', name: 'Bagmati', description: 'Province', usage: provinceUsage['Bagmati'] || 0, status: 'active' },
      { id: 'gandaki', name: 'Gandaki', description: 'Province', usage: provinceUsage['Gandaki'] || 0, status: 'active' },
      { id: 'karnali', name: 'Karnali', description: 'Province', usage: provinceUsage['Karnali'] || 0, status: 'active' },
      { id: 'koshi', name: 'Koshi', description: 'Province', usage: provinceUsage['Koshi'] || 0, status: 'active' },
      { id: 'lumbini', name: 'Lumbini', description: 'Province', usage: provinceUsage['Lumbini'] || 0, status: 'active' },
      { id: 'madhesh', name: 'Madhesh', description: 'Province', usage: provinceUsage['Madhesh'] || 0, status: 'active' },
      { id: 'sudurpashchim', name: 'Sudurpashchim', description: 'Province', usage: provinceUsage['Sudurpashchim'] || 0, status: 'active' }
    ],
    ownership: [
      { id: '1', name: 'Private Limited', description: 'Private limited company', usage: ownershipUsage['Private Limited'] || ownershipUsage['Private'] || 0, status: 'active' },
      { id: '2', name: 'Public Limited', description: 'Public limited company', usage: ownershipUsage['Public Limited'] || ownershipUsage['Public'] || 0, status: 'active' },
      { id: '3', name: 'Partnership', description: 'Partnership firm', usage: ownershipUsage['Partnership'] || 0, status: 'active' },
      { id: '4', name: 'Sole Proprietorship', description: 'Individual ownership', usage: ownershipUsage['Sole Proprietorship'] || 0, status: 'active' },
      { id: '5', name: 'Government', description: 'Government organization', usage: ownershipUsage['Government'] || 0, status: 'active' },
      { id: '6', name: 'NGO/Non-Profit', description: 'Non-governmental organization', usage: ownershipUsage['NGO/Non-Profit'] || ownershipUsage['Non-profit'] || 0, status: 'active' },
      { id: '7', name: 'Cooperative', description: 'Cooperative society', usage: ownershipUsage['Cooperative'] || 0, status: 'active' },
      { id: '8', name: 'Multinational', description: 'Multinational corporation', usage: ownershipUsage['Multinational'] || 0, status: 'active' }
    ],
    sizes: [
      { id: '1', name: '1-10 employees', range: '1-10', description: 'Startup/Small business', usage: sizeUsage['1-10'] || 0, status: 'active' },
      { id: '2', name: '11-50 employees', range: '11-50', description: 'Small company', usage: sizeUsage['11-50'] || 0, status: 'active' },
      { id: '3', name: '51-200 employees', range: '51-200', description: 'Medium company', usage: sizeUsage['51-200'] || 0, status: 'active' },
      { id: '4', name: '201-500 employees', range: '201-500', description: 'Large company', usage: sizeUsage['201-500'] || 0, status: 'active' },
      { id: '5', name: '501-1000 employees', range: '501-1000', description: 'Enterprise company', usage: sizeUsage['501-1000'] || 0, status: 'active' },
      { id: '6', name: '1000+ employees', range: '1000+', description: 'Large enterprise', usage: sizeUsage['1000+'] || 0, status: 'active' }
    ],
    vehicles: [
      { id: '1', name: 'Two Wheeler', description: 'Motorcycle, scooter provided', usage: vehicleUsage['two wheeler'] || 0, status: 'active' },
      { id: '2', name: 'Four Wheeler', description: 'Car provided for transportation', usage: vehicleUsage['four wheeler'] || 0, status: 'active' },
      { id: '3', name: 'Company Vehicle', description: 'Official company vehicle', usage: vehicleUsage['company vehicle'] || 0, status: 'active' },
      { id: '4', name: 'Transport Allowance', description: 'Monthly transport allowance', usage: vehicleUsage['transport allowance'] || 0, status: 'active' },
      { id: '5', name: 'Fuel Allowance', description: 'Fuel reimbursement provided', usage: vehicleUsage['fuel allowance'] || 0, status: 'active' },
      { id: '6', name: 'Public Transport', description: 'Public transport ticket/pass', usage: vehicleUsage['public transport'] || 0, status: 'active' },
      { id: '7', name: 'No Vehicle', description: 'No vehicle benefits', usage: vehicleUsage['no vehicle'] || 0, status: 'active' }
    ],
    locations: [
      { id: '1', name: 'Kathmandu', description: 'Capital city', usage: locationUsage['Kathmandu'] || 0, status: 'active', province: 'Bagmati' },
      { id: '2', name: 'Pokhara', description: 'Tourism city', usage: locationUsage['Pokhara'] || 0, status: 'active', province: 'Gandaki' },
      { id: '3', name: 'Lalitpur', description: 'Patan city', usage: locationUsage['Lalitpur'] || 0, status: 'active', province: 'Bagmati' },
      { id: '4', name: 'Bhaktapur', description: 'Historic city', usage: locationUsage['Bhaktapur'] || 0, status: 'active', province: 'Bagmati' },
      { id: '5', name: 'Chitwan', description: 'Industrial and tourism', usage: locationUsage['Chitwan'] || 0, status: 'active', province: 'Bagmati' }
    ]
  });

  async function refreshRemote(tab: 'industries' | 'ownership' | 'locations' | 'countries' | 'provinces' | 'sizes' | 'vehicles') {
    try {
      const type = tab === 'industries' ? 'industry' :
        tab === 'ownership' ? 'company_type' :
          tab === 'countries' ? 'country' :
            tab === 'provinces' ? 'province' :
              tab === 'locations' ? 'location' :
                tab === 'sizes' ? 'company_size' :
                  tab === 'vehicles' ? 'vehicle_benefit' : 'industry';

      const res = await companyParameterService.list(type);
      const items = (res.items || res).map((i: any) => ({
        id: i._id || i.id,
        name: i.name,
        description: i.description || '',
        usage: i.usage || (
          tab === 'industries' ? (industryUsage[i.name] || 0) :
            tab === 'ownership' ? (ownershipUsage[i.name] || 0) :
              tab === 'locations' ? (locationUsage[i.name] || 0) :
                tab === 'countries' ? (countryUsage[i.name] || 0) :
                  tab === 'provinces' ? (provinceUsage[i.name] || 0) :
                    tab === 'sizes' ? (sizeUsage[i.range || ''] || 0) :
                      tab === 'vehicles' ? (vehicleUsage[(i.name || '').toLowerCase()] || 0) : 0
        ),
        status: i.status || 'active',
        range: i.range || '',
        province: i.province || i.parentProvince || ''
      }));
      setParameters(prev => ({ ...prev, [tab]: items }));
    } catch (err) {
      // keep local mock if backend not ready
    }
  }

  useEffect(() => {
    if (remoteTabs.includes(activeTab as any)) {
      refreshRemote(activeTab as any);
    }
  }, [activeTab]);

  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    range: ''
  });
  const [newItemParent, setNewItemParent] = useState<string>('');

  const parameterConfigs: Record<keyof ParameterMap, Config> = {
    industries: {
      title: 'Industries Management',
      description: 'Manage industry categories and classifications',
      icon: Factory,
      fields: ['name', 'description'],
      columns: ['Industry', 'Description', 'Usage', 'Status', 'Actions']
    },
    countries: {
      title: 'Country',
      description: 'Manage countries for locations',
      icon: MapPin,
      fields: ['name'],
      columns: ['Country', 'Description', 'Usage', 'Status', 'Actions']
    },
    provinces: {
      title: 'Province',
      description: 'Manage provinces linked to countries',
      icon: MapPin,
      fields: ['name'],
      columns: ['Province', 'Description', 'Usage', 'Status', 'Actions']
    },
    ownership: {
      title: 'Company Ownership',
      description: 'Manage company ownership types',
      icon: Crown,
      fields: ['name', 'description'],
      columns: ['Ownership Type', 'Description', 'Usage', 'Status', 'Actions']
    },
    sizes: {
      title: 'Company Size',
      description: 'Manage company size categories',
      icon: Ruler,
      fields: ['name', 'range', 'description'],
      columns: ['Size Category', 'Range', 'Description', 'Usage', 'Actions']
    },
    vehicles: {
      title: 'Vehicle Benefits',
      description: 'Manage vehicle and transport benefits',
      icon: Car,
      fields: ['name', 'description'],
      columns: ['Vehicle Type', 'Description', 'Usage', 'Status', 'Actions']
    },
    locations: {
      title: 'Location',
      description: 'Manage city locations for jobs and employers',
      icon: MapPin,
      fields: ['name'],
      columns: ['City', 'Province', 'Usage', 'Status', 'Actions']
    }
  };

  const currentConfig = parameterConfigs[activeTab as keyof typeof parameterConfigs];
  const currentData = parameters[activeTab as keyof typeof parameters] as ParamItem[];

  const filteredData = currentData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    ((item as any).range && (item as any).range.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const [provinceFilter, setProvinceFilter] = useState<string>('');
  const [provinceView, setProvinceView] = useState<string>('');
  const filteredByProvince = (activeTab === 'locations' && provinceFilter)
    ? filteredData.filter(item => String((item as any).province || '').trim() === provinceFilter)
    : filteredData;
  const totalPages = Math.max(1, Math.ceil(filteredByProvince.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredByProvince.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const handleAdd = async () => {
    if (!newItem.name) {
      toast.error('Name is required');
      return;
    }
    if (remoteTabs.includes(activeTab as any)) {
      try {
        const type = activeTab === 'industries' ? 'industry' :
          activeTab === 'ownership' ? 'company_type' :
            activeTab === 'provinces' ? 'province' :
              activeTab === 'locations' ? 'location' :
                activeTab === 'countries' ? 'country' :
                  activeTab === 'sizes' ? 'company_size' :
                    activeTab === 'vehicles' ? 'vehicle_benefit' : '';

        if (!type) throw new Error('Invalid type');

        const payload: any = { name: newItem.name, description: newItem.description };
        if (activeTab === 'provinces' && newItemParent) payload.country = newItemParent;
        if (activeTab === 'locations' && newItemParent) payload.province = newItemParent;
        if (activeTab === 'sizes' && newItem.range) payload.range = newItem.range;

        await companyParameterService.create(type, payload);
        await refreshRemote(activeTab as any);

        // Refresh global data context
        if (activeTab === 'industries') refreshAdminIndustries();
        if (activeTab === 'countries') refreshAdminCountries();
        if (activeTab === 'provinces') refreshAdminProvinces();
        if (activeTab === 'locations') refreshAdminCities();

        toast.success('Item added successfully');
      } catch (err) {
        toast.error('Failed to add item');
        return;
      }
    } else {
      const item = {
        id: Date.now().toString(),
        ...newItem,
        usage: 0,
        status: 'active'
      };
      setParameters(prev => ({
        ...prev,
        [activeTab]: [...prev[activeTab as keyof typeof prev], item]
      }));
      toast.success(`${currentConfig.title} item added successfully!`);
    }

    setNewItem({ name: '', description: '', range: '' });
    setNewItemParent('');
    setIsAddDialogOpen(false);
  };

  const handleEdit = (item: any) => {
    setEditingItem({ ...item });
  };

  const handleUpdate = async () => {
    if (!editingItem || !editingItem.name) {
      toast.error('Name is required');
      return;
    }
    if (remoteTabs.includes(activeTab as any)) {
      try {
        const type = activeTab === 'industries' ? 'industry' :
          activeTab === 'ownership' ? 'company_type' :
            activeTab === 'countries' ? 'country' :
              activeTab === 'provinces' ? 'province' :
                activeTab === 'locations' ? 'location' :
                  activeTab === 'sizes' ? 'company_size' :
                    activeTab === 'vehicles' ? 'vehicle_benefit' : '';

        const payload: any = {
          name: editingItem.name,
          description: editingItem.description,
          status: editingItem.status,
        };

        if (activeTab === 'sizes' && editingItem.range) {
          payload.range = editingItem.range;
        }

        await companyParameterService.update(type, editingItem.id, payload);
        await refreshRemote(activeTab as any);

        // Refresh global data context
        if (activeTab === 'industries') refreshAdminIndustries();
        if (activeTab === 'countries') refreshAdminCountries();
        if (activeTab === 'provinces') refreshAdminProvinces();
        if (activeTab === 'locations') refreshAdminCities();

        toast.success('Item updated successfully');
      } catch (err) {
        toast.error('Failed to update item');
        return;
      }
    } else {
      setParameters(prev => ({
        ...prev,
        [activeTab]: prev[activeTab as keyof typeof prev].map(item =>
          item.id === editingItem.id ? editingItem : item
        )
      }));
      toast.success(`${currentConfig.title} item updated successfully!`);
    }

    setEditingItem(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirmDelete()) return;
    if (remoteTabs.includes(activeTab as any)) {
      try {
        const type = activeTab === 'industries' ? 'industry' :
          activeTab === 'ownership' ? 'company_type' :
            activeTab === 'countries' ? 'country' :
              activeTab === 'provinces' ? 'province' :
                activeTab === 'locations' ? 'location' :
                  activeTab === 'sizes' ? 'company_size' :
                    activeTab === 'vehicles' ? 'vehicle_benefit' : '';
        await companyParameterService.remove(type, id);
        await refreshRemote(activeTab as any);

        // Refresh global data context
        if (activeTab === 'industries') refreshAdminIndustries();
        if (activeTab === 'countries') refreshAdminCountries();
        if (activeTab === 'provinces') refreshAdminProvinces();
        if (activeTab === 'locations') refreshAdminCities();

        toast.success('Item deleted successfully');
      } catch (err) {
        toast.error('Failed to delete item');
      }
      return;
    }
    setParameters(prev => ({
      ...prev,
      [activeTab]: prev[activeTab as keyof typeof prev].filter(item => item.id !== id)
    }));
    toast.success(`${currentConfig.title} item deleted successfully!`);
  };

  const toggleStatus = async (id: string) => {
    if (remoteTabs.includes(activeTab as any)) {
      const items = parameters[activeTab as keyof typeof parameters] as any[];
      const item = items.find(i => i.id === id);
      const newStatus = item?.status === 'active' ? 'inactive' : 'active';
      try {
        const type = activeTab === 'industries' ? 'industry' :
          activeTab === 'ownership' ? 'company_type' :
            activeTab === 'countries' ? 'country' :
              activeTab === 'provinces' ? 'province' :
                activeTab === 'locations' ? 'location' :
                  activeTab === 'sizes' ? 'company_size' :
                    activeTab === 'vehicles' ? 'vehicle_benefit' : '';
        await companyParameterService.update(type, id, { status: newStatus });
        await refreshRemote(activeTab as any);

        // Refresh global data context
        if (activeTab === 'industries') refreshAdminIndustries();
        if (activeTab === 'countries') refreshAdminCountries();
        if (activeTab === 'provinces') refreshAdminProvinces();
        if (activeTab === 'locations') refreshAdminCities();

        toast.success('Status updated successfully!');
      } catch (err) {
        toast.error('Failed to update status');
      }
      return;
    }
    setParameters(prev => ({
      ...prev,
      [activeTab]: prev[activeTab as keyof typeof prev].map(item =>
        item.id === id
          ? { ...item, status: item.status === 'active' ? 'inactive' : 'active' }
          : item
      )
    }));
    toast.success('Status updated successfully!');
  };

  const renderTableRow = (item: ParamItem) => {
    switch (activeTab) {
      case 'sizes':
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50 text-xs">
            <td className="py-1.5 px-2 font-medium">{item.name}</td>
            <td className="py-1.5 px-2">
              <Badge variant="outline" className="text-[11px] px-2 py-0.5">{item.range}</Badge>
            </td>
            <td className="py-1.5 px-2 text-xs text-gray-600">{item.description}</td>
            <td className="py-1.5 px-2 text-xs">{item.usage} companies</td>
            <td className="py-1.5 px-2">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={(item.status === 'active' ? 'text-red-600' : 'text-green-600') + ' h-7 px-2'}
                  onClick={() => toggleStatus(item.id)}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-red-600 hover:text-red-700"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );

      default:
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50 text-xs text-nowrap">
            <td className="py-1.5 px-2 font-medium">{item.name}</td>
            <td className="py-1.5 px-2 text-xs text-gray-600">
              {activeTab === 'locations' ? (
                item.province ? (
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-100 font-normal shadow-none">
                    {item.province}
                  </Badge>
                ) : (
                  <span className="text-gray-400 italic">No Province</span>
                )
              ) : (
                item.description
              )}
            </td>
            <td className="py-1.5 px-2 text-xs">
              <span className="font-semibold text-gray-700">{item.usage}</span>
              <span className="text-gray-500 ml-1">
                {(activeTab === 'vehicles' || activeTab === 'industries') ? 'jobs' : 'entities'}
              </span>
            </td>
            <td className="py-1.4 px-2">
              <Badge className={(item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800') + ' text-[11px] px-2 py-0.5 shadow-none border-0'}>
                {item.status}
              </Badge>
            </td>
            <td className="py-1.5 px-2">
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={(item.status === 'active' ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50') + ' h-7 px-2'}
                  onClick={() => toggleStatus(item.id)}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );
    }
  };

  const renderAddForm = () => (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="itemName" className="text-xs">Name</Label>
        <Input
          id="itemName"
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          placeholder={`Enter ${activeTab.slice(0, -1)} name`}
          className="h-8 text-xs"
        />
      </div>

      {currentConfig.fields.includes('range') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemRange" className="text-xs">Range</Label>
          <Input
            id="itemRange"
            value={newItem.range}
            onChange={(e) => setNewItem({ ...newItem, range: e.target.value })}
            placeholder="e.g., 1-10, 11-50"
            className="h-8 text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('description') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemDescription" className="text-xs">Description</Label>
          <Textarea
            id="itemDescription"
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Enter description"
            rows={3}
            className="text-xs"
          />
        </div>
      )}
      {activeTab === 'provinces' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Country</Label>
          <Select value={newItemParent || ''} onValueChange={(value) => setNewItemParent(value)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {(parameters.countries || []).map((c) => (
                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {activeTab === 'locations' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Province</Label>
          <Select value={newItemParent || ''} onValueChange={(value) => setNewItemParent(value)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select province" />
            </SelectTrigger>
            <SelectContent>
              {(parameters.provinces || []).map((p) => (
                <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  const renderEditForm = () => (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="editItemName" className="text-xs">Name</Label>
        <Input
          id="editItemName"
          value={editingItem?.name || ''}
          onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
          className="h-8 text-xs"
        />
      </div>

      {currentConfig.fields.includes('range') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemRange" className="text-xs">Range</Label>
          <Input
            id="editItemRange"
            value={editingItem?.range || ''}
            onChange={(e) => setEditingItem({ ...editingItem, range: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('description') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemDescription" className="text-xs">Description</Label>
          <Textarea
            id="editItemDescription"
            value={editingItem?.description || ''}
            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
            rows={3}
            className="text-xs"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h2 className="text-2xl font-bold">Company Parameter Management</h2>
          <p className="text-gray-600">Manage company-related parameters and classifications</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="industries">Industries</TabsTrigger>
          <TabsTrigger value="countries">Countries</TabsTrigger>
          <TabsTrigger value="provinces">Provinces</TabsTrigger>
          <TabsTrigger value="ownership">Ownership</TabsTrigger>
          <TabsTrigger value="sizes">Company Size</TabsTrigger>
          <TabsTrigger value="vehicles">Vehicle</TabsTrigger>
          <TabsTrigger value="locations">Location</TabsTrigger>
        </TabsList>

        {Object.keys(parameterConfigs).map(tabKey => (
          <TabsContent key={tabKey} value={tabKey} className="space-y-3">
            {/* Tab Header */}
            <Card>
              <CardContent className="pt-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div className="flex items-center gap-3">
                    {React.createElement(parameterConfigs[tabKey as keyof typeof parameterConfigs].icon, { className: "h-8 w-8 text-blue-600" })}
                    <div>
                      <h3 className="text-lg font-semibold">{currentConfig.title}</h3>
                      <p className="text-sm text-gray-600">{currentConfig.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {tabKey === 'locations' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">Province</Label>
                        <Select value={provinceFilter || undefined} onValueChange={(value) => { setProvinceFilter(value === '__ALL__' ? '' : value); setCurrentPage(1); }}>
                          <SelectTrigger className="h-8 text-xs w-44">
                            <SelectValue placeholder="All provinces" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__ALL__">All provinces</SelectItem>
                            {(parameters.provinces || []).map((p) => (
                              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {tabKey === 'provinces' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs">View Cities</Label>
                        <Select value={provinceView || undefined} onValueChange={(value) => { setProvinceView(value === '__NONE__' ? '' : value); }}>
                          <SelectTrigger className="h-8 text-xs w-52">
                            <SelectValue placeholder="Select province" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__">Select province</SelectItem>
                            {(parameters.provinces || []).map((p) => (
                              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64 h-8 text-xs"
                      />
                    </div>
                    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="h-8 text-xs">
                          <Plus className="h-4 w-4 mr-2" />
                          Add {tabKey.slice(0, -1)}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="p-4">
                        <DialogHeader>
                          <DialogTitle>Add {currentConfig.title}</DialogTitle>
                          <DialogDescription>
                            Create a new {currentConfig.title.toLowerCase()} entry.
                          </DialogDescription>
                        </DialogHeader>
                        {renderAddForm()}
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleAdd}>Add</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Items</p>
                      <p className="text-2xl font-bold text-blue-600">{currentData.length}</p>
                    </div>
                    {React.createElement(currentConfig.icon, { className: 'h-8 w-8 text-blue-600' })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active</p>
                      <p className="text-2xl font-bold text-green-600">
                        {currentData.filter(item => item.status === 'active').length}
                      </p>
                    </div>
                    {React.createElement(currentConfig.icon, { className: 'h-8 w-8 text-green-600' })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Most Used</p>
                      <p className="text-lg font-bold text-purple-600">
                        {currentData.reduce((max, item) => item.usage > max.usage ? item : max, currentData[0])?.name || 'N/A'}
                      </p>
                    </div>
                    {React.createElement(currentConfig.icon, { className: 'h-8 w-8 text-purple-600' })}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Usage</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {currentData.reduce((sum, item) => sum + item.usage, 0)}
                      </p>
                    </div>
                    {React.createElement(currentConfig.icon, { className: 'h-8 w-8 text-orange-600' })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {tabKey === 'provinces' && provinceView && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Cities in {provinceView} ({(parameters.locations || []).filter((c: any) => String(c.province || '').trim() === provinceView).length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-1">
                    {(parameters.locations || [])
                      .filter((c: any) => String(c.province || '').trim() === provinceView)
                      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')))
                      .map((c: any) => (
                        <div key={c.id} className="flex items-center justify-between px-2 py-1 border rounded">
                          <div className="text-xs font-medium truncate">{c.name}</div>
                          <span className={`inline-block w-2 h-2 rounded-full ${String(c.status || 'active') === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data Table */}
            <Card>
              <CardHeader>
                <CardTitle>{currentConfig.title} ({filteredData.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        {currentConfig.columns.map(column => (
                          <th key={column} className="text-left py-2 px-2 text-xs">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map(item => renderTableRow(item))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span>Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="px-2 py-1 bg-white border rounded"
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</Button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="p-4">
          <DialogHeader>
            <DialogTitle>Edit {currentConfig.title}</DialogTitle>
            <DialogDescription>
              Update the {currentConfig.title.toLowerCase()} information.
            </DialogDescription>
          </DialogHeader>
          {renderEditForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
